/**
 * The editor's budget fields. A definition without a budget runs at the
 * server's maximum, so that is what the placeholders show, and the bounds
 * come from the editor options (backend `BudgetSpec` and the dispatch
 * caps). Without the options they fall back to the word "default".
 */

import type { ReactElement } from 'react';
import type { EditorOptions } from '../schemas/editorOptions.schemas';

interface TextInput {
  id: string;
  value: string;
  onChange: (e: { target: { value: string } }) => void;
}

interface BudgetFieldsProps {
  id: string;
  minutes: TextInput;
  tokens: TextInput;
  budget: EditorOptions['budget'] | undefined;
}

const count = (n: number): string => n.toLocaleString('en-US');

export function BudgetFields({ id, minutes, tokens, budget }: BudgetFieldsProps): ReactElement {
  const hint = `${id}-budget-hint`;
  return (
    <div className="def-form__row">
      <div className="form-group def-form__field">
        <label htmlFor={minutes.id}>Budget minutes</label>
        <input
          type="number"
          {...minutes}
          min={budget?.minutes_min ?? 1}
          max={budget?.minutes_max ?? 20}
          placeholder={budget ? String(budget.minutes_max) : 'default'}
          aria-describedby={hint}
        />
      </div>
      <div className="form-group def-form__field">
        <label htmlFor={tokens.id}>Budget tokens</label>
        <input
          type="number"
          {...tokens}
          min={budget?.tokens_min ?? 1000}
          max={budget?.tokens_max ?? 200000}
          step={1000}
          placeholder={budget ? String(budget.tokens_max) : 'default'}
          aria-describedby={hint}
        />
      </div>
      <span id={hint} className="def-form__hint def-form__row-hint">
        {budget
          ? `Default and maximum: ${count(budget.minutes_max)} minutes and ${count(budget.tokens_max)} tokens (at least ${count(budget.minutes_min)} minute and ${count(budget.tokens_min)} tokens).`
          : 'Leave empty for the server default.'}
      </span>
    </div>
  );
}
